'use client';
import {
  insetMessageImageWidths,
  MESSAGE_IMAGE_LAYOUT,
  type MessageImageLayoutWidths,
} from '@ui/hooks/file/message-image-layout';
import type {
  IMessage,
  IMessagesQueryParams,
} from '@ui/components/organisms/message/message.typings';
import { PostItemAuthor } from './post-item-author';
import { ContentType, ContextUserType } from '@ui/constants/context';
import { useMessageContext } from '@ui/components/organisms/message/message.context';
import { MessageItemDescription } from '@ui/components/organisms/message/components/message-list/message-item-description';
import { PostItemsMoreOptions } from './post-item-more-option';
import { MessageItemFiles } from '@ui/components/organisms/message/components/message-list/message-item-files';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import React, { ReactNode, useRef, forwardRef, useId, useCallback, useState, useMemo } from 'react';
import { PostItemReactions } from './post-item-reactions';
import { twMerge } from 'tailwind-merge';
import { useMessageUpdateFormContext } from '@ui/contexts/message-update-form-context';
import { ItemUpdateMessageContentWrapper } from '../common/item-update-message-content-wrapper';
import { Icon } from '@ui/components/atoms/icon/icon';
import { useToast } from '@ui/components/atoms/toast/toast';
import { MessageBlocked } from '../common/message-blocked';
import { MessagePoll } from '../common/message-poll';
import { PinIcon } from 'lucide-react';
import { DynamicRouteBuilder } from '@ui/constants/dynamic-route-builder';
import { trackPostClicked } from '../../../../../analytics/utils';
import {
  claimPostDetailPrefetchIntent,
  isUnmodifiedPrimaryClick,
  stagePostDetailHandoff,
} from './post-detail-handoff';
import dynamic from 'next/dynamic';

const PostItemAnalyticsModal = dynamic(() => import('./post-item-analytics'));

interface Props {
  post: IMessage;
  userType: ContextUserType;
  userId: string;
  creator_id: string;
  communityPostRoute: string;
  isOwnPage?: boolean;
  /** Exact route has not yet authorized mutation or reply controls. */
  isReadOnly?: boolean;
  isLastPost?: boolean;
  query: IMessagesQueryParams;
  contentType: ContentType;
  isDynamicRoute?: boolean;
  communityDetails: NonNullable<IMessage['community_details']>;
  moderators_ids: string[];
  attachmentLoading?: 'eager' | 'lazy';
  reserveAttachmentSpace?: boolean;
  attachmentImageLayoutWidths?: MessageImageLayoutWidths;
}

interface ConditionalWrapperProps {
  condition: boolean;
  wrapper: (children: ReactNode) => JSX.Element;
  children: ReactNode;
  id: string;
}

const ConditionalPostLinkWrapper = forwardRef<HTMLDivElement, ConditionalWrapperProps>(
  ({ condition, wrapper, children }, ref) =>
    condition ? <div ref={ref}>{wrapper(children)}</div> : <div ref={ref}>{children}</div>,
);

ConditionalPostLinkWrapper.displayName = 'ConditionalPostLinkWrapper';

const PostItem = ({
  post,
  userType,
  userId,
  communityPostRoute,
  isOwnPage,
  isReadOnly = false,
  isLastPost,
  query,
  creator_id,
  contentType,
  isDynamicRoute,
  communityDetails,
  moderators_ids,
  attachmentLoading = 'eager',
  reserveAttachmentSpace = false,
  attachmentImageLayoutWidths,
}: Props) => {
  const imageLayoutWidths = useMemo(
    () =>
      insetMessageImageWidths(
        attachmentImageLayoutWidths || {
          default: {
            viewportRatio: 1,
            offsetPx: 0,
            ...(isOwnPage ? { maxPx: MESSAGE_IMAGE_LAYOUT.detailMax } : {}),
          },
        },
        isOwnPage ? 0 : MESSAGE_IMAGE_LAYOUT.postInset,
      ),
    [attachmentImageLayoutWidths, isOwnPage],
  );
  const id = useId();
  const router = useRouter();
  const toast = useToast();
  const updateRef = useRef<HTMLDivElement>(null);
  const { setTargetMessage } = useMessageUpdateFormContext();
  const [dropDownOpen, setDropDownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const messageState = useMessageContext('creator');

  const isOptimisticPost = useMemo(() => {
    const idIsTemp = typeof post?.id === 'string' && post.id.startsWith('temp-');
    const slugIsTemp = typeof post?.slug === 'string' && post.slug.startsWith('temp-');
    const processingStatus =
      typeof (post as any)?.miscellaneous?.processing_status === 'string' ||
      typeof (post as any)?.processing_status === 'string';
    return idIsTemp || slugIsTemp || processingStatus;
  }, [post]);

  const onClickOptimisticPost = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      toast(
        {
          title: 'Post is still processing',
          message: 'Please wait a moment and try again.',
        },
        { type: 'info' as any },
      );
    },
    [toast],
  );

  const onUpdateTrigger = useCallback(() => {
    if (updateRef.current) {
      setTargetMessage({
        ...post,
        isPost: true,
        targetRef: updateRef.current,
        type: 'post',
        query,
      });
    }
  }, [post, setTargetMessage]);

  const onOpenPostAnalytics = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      messageState.onGetPostAnalytics(post?.id);
      setTimeout(() => {
        setIsModalOpen(true);
      }, 0);
    },
    [post?.id, messageState],
  );

  // Canonical URL identity for the community segment. `conversation_id` is a
  // database UUID, so it is only an explicit compatibility fallback when no
  // canonical slug exists — never silently relabeled as a slug.
  const conversationRouteToken =
    communityDetails?.conversation_slug ||
    post.conversation_id ||
    communityDetails?.conversation_id ||
    query?.conversation_id;

  // WP-01 bounded compatibility telemetry: enum-only signal of whether the
  // rendered URL needed the UUID fallback because no canonical slug exists.
  // Never carries the UUID, slug, or any other route/entity value.
  const conversationRouteIdentity = communityDetails?.conversation_slug
    ? 'canonical_slug'
    : 'compatibility_id';

  const postLink = useMemo(() => {
    // Avoid generating routes to temp IDs — these pages will 404 / show empty content.
    if (isOptimisticPost) return '';
    if (isDynamicRoute) {
      const route = DynamicRouteBuilder.postBySlug(
        communityDetails.creator_display_name || communityDetails.creator_id,
        conversationRouteToken,
        post.slug || post.id,
      );
      // return `/creators/${communityDetails.creator_display_name || communityDetails.creator_id}/communities/${conversationId}/posts/${post.slug || post.id}`;
      return route;
    }
    // Handle bad post route
    if (communityPostRoute.includes('undefined')) {
      if (communityDetails?.conversation_type === 'community') {
        // replace the first undefined with the community id
        // http://localhost:3001/creators/KOR_Crew/communities/undefined/posts/undefined/?slug=be-part-of-building-kor-crew-zkd1ac
        // replace the second undefined with the post id
        // http://localhost:3001/creators/KOR_Crew/communities/undefined/posts/undefined/?slug=be-part-of-building-kor-crew-zkd1ac
        return communityPostRoute
          .replace('undefined', communityDetails.conversation_id)
          .replace('undefined', post.id);
      }
      console.warn('[BAD_COMMUNITY_URL_CAUGHT]', communityPostRoute);
    }
    return `${communityPostRoute}?slug=${post?.slug || post?.id}`;
  }, [
    communityPostRoute,
    post?.slug,
    post?.id,
    post?.conversation_id,
    conversationRouteToken,
    communityDetails?.conversation_id,
    communityDetails?.creator_display_name,
    communityDetails?.creator_id,
    communityDetails?.conversation_type,
    query?.conversation_id,
    isDynamicRoute,
    userType,
    isOptimisticPost,
  ]);

  // Exact tokens encoded into the dynamic href, staged on unmodified primary
  // clicks so the detail route can verify identity before reusing this row.
  const postRouteTokens = useMemo(() => {
    if (!isDynamicRoute) return null;
    return {
      creator: communityDetails.creator_display_name || communityDetails.creator_id,
      conversation: conversationRouteToken,
      post: post.slug || post.id,
    };
  }, [
    isDynamicRoute,
    communityDetails?.creator_display_name,
    communityDetails?.creator_id,
    post?.conversation_id,
    post?.slug,
    post?.id,
    conversationRouteToken,
    communityDetails?.conversation_id,
    query?.conversation_id,
  ]);
  // Feed responses may keep community identity at the response level instead
  // of repeating it on every row. Bind that already-rendered context into the
  // short-lived handoff so the detail route can verify the clicked content
  // against the URL without treating an ambient list row as authority.
  const postDetailHandoffMessage = useMemo<IMessage>(
    () => ({
      ...post,
      community_details: {
        ...communityDetails,
        conversation_id:
          post.conversation_id || communityDetails.conversation_id || query?.conversation_id,
        // The canonical slug passes through verbatim; a UUID compatibility
        // token must never be written into it. Route verification relies on
        // the exact tokens, matching conversation_id when no slug exists.
      },
    }),
    [communityDetails, post, post.conversation_id, query?.conversation_id],
  );
  const prefetchPostDetailOnIntent = useCallback(() => {
    if (!isDynamicRoute || !postRouteTokens || !postLink) return;
    const messageId = post.slug || post.id;
    if (!claimPostDetailPrefetchIntent(`${postLink}|${messageId}`)) return;
    void router.prefetch(postLink);
    messageState.prefetchSingleMessage?.(messageId);
  }, [
    isDynamicRoute,
    messageState.prefetchSingleMessage,
    post.id,
    post.slug,
    postLink,
    postRouteTokens,
    router,
  ]);
  const enableContentFlagging = useMemo(() => {
    if (post.user_id === userId) return false;
    if (userType === 'creator') return false;
    if (communityDetails.creator_id === userId) return false;

    return true;
  }, [post.creator_id, communityDetails.creator_id, post.user_id, userId]);

  const hasImages = useMemo(
    () => Boolean(post?.files && post.files.images && post.files.images.length > 0),
    [post?.files],
  );

  // For posts with images that are still in a processing/optimistic state, do not
  // render the card at all. The post will appear in the feed only after the
  // background job completes and the real, persisted post is delivered via
  // the message_processing_status websocket flow.
  if (isOptimisticPost && hasImages) {
    return null;
  }

  return (
    <ItemUpdateMessageContentWrapper ref={updateRef}>
      <div className={twMerge('flex flex-col', !isOwnPage && 'px-4 py-2 border rounded')}>
        <ConditionalPostLinkWrapper
          id={id}
          condition={!isOwnPage && !isOptimisticPost}
          wrapper={(children) => (
            <Link
              href={postLink}
              onPointerEnter={prefetchPostDetailOnIntent}
              onFocus={prefetchPostDetailOnIntent}
              onTouchStart={prefetchPostDetailOnIntent}
              onClick={(event) => {
                trackPostClicked({
                  postId: post.id,
                  creatorId: creator_id,
                  userId,
                  contentType: String(contentType),
                  metadata: { conversation_route_identity: conversationRouteIdentity },
                });
                // Stage the warm handoff only for unmodified primary clicks on
                // dynamic internal links; modified/new-tab behavior stays normal.
                if (postRouteTokens && isUnmodifiedPrimaryClick(event)) {
                  stagePostDetailHandoff(
                    postDetailHandoffMessage,
                    postRouteTokens,
                    Date.now(),
                    userId,
                  );
                }
              }}
              className={twMerge('block space-y-4 bg-white', isLastPost && 'border-b-0')}>
              {children}
            </Link>
          )}>
          <div
            className={twMerge('space-y-4', 'flex flex-col justify-between')}
            onClick={isOptimisticPost && !isOwnPage ? onClickOptimisticPost : undefined}>
            <div className="flex justify-between">
              <PostItemAuthor
                post={post}
                postOwnerUserType={post.user_type === 2 ? 'creator' : 'fan'}
                userType={userType}
                creatorId={creator_id}
                isDynamicRoute={isDynamicRoute}
                communityDetails={communityDetails}
                moderators_ids={moderators_ids}
                isInsideLink={!isOwnPage}
              />
              <div className="flex items-center">
                {post.pinned_at && (
                  <PinIcon className="text-primaryActive font-thin h-4 w-4 fill-white rotate-12" />
                )}
                <div className="flex items-center">
                  {userType === 'creator' && !isReadOnly && !post.draft && !isOptimisticPost && (
                    <div
                      className="p-2 cursor-pointer rounded hover:bg-gray-200"
                      onClick={onOpenPostAnalytics}>
                      <Icon type="slimGraph" />
                    </div>
                  )}
                  {!isReadOnly && (
                    <PostItemsMoreOptions
                      post={post}
                      userType={userType}
                      userId={userId}
                      enableContentFlagging={enableContentFlagging}
                      onUpdateTrigger={onUpdateTrigger}
                      dropdownOpen={dropDownOpen}
                      setDropdownOpen={setDropDownOpen}
                      contentType={contentType}
                      communityDetails={communityDetails}
                      moderators_ids={moderators_ids}
                      reportLabel="Report post"
                      disableEditDelete={isOptimisticPost}
                    />
                  )}
                </div>
              </div>
            </div>
            {!post.block && post.name && (
              <p className="font-nunito text-md font-bold" style={{ overflowWrap: 'break-word' }}>
                {post.name}
              </p>
            )}

            {isOptimisticPost && hasImages && (
              <div className="text-xs text-gray-500">
                Processing post with image… This post will be available to open shortly.
              </div>
            )}

            {post.block && (
              <MessageBlocked userType={userType} insideLink={!isOwnPage} creatorId={creator_id} />
            )}

            {!post.block && (
              <MessageItemDescription
                truncate
                message={post}
                isOwnPage={isOwnPage}
                className="max-w-full rounded-none bg-transparent p-0"
              />
            )}

            {!post.block && post?.files && post?.files?.images?.length > 0 && (
              <MessageItemFiles
                className="max-w-full"
                files={post.files}
                imageLoading={attachmentLoading}
                responsiveImageVariants
                reserveImageSpace={reserveAttachmentSpace}
                imageLayoutWidths={imageLayoutWidths}
              />
            )}

            <MessagePoll
              message={post}
              userType={userType}
              readOnly={isReadOnly}
              creatorDisplayName={communityDetails?.creator_display_name}
            />
          </div>

          {!isReadOnly && !post.draft && !post.block && (
            <div className="pt-4">
              <PostItemReactions
                post={post}
                userType={userType}
                hierarchyType="post"
                onTriggerReplyForm={() => {}}
                enableReply={contentType === 'post'}
              />
            </div>
          )}
        </ConditionalPostLinkWrapper>

        {isModalOpen && (
          <PostItemAnalyticsModal
            isOpen={isModalOpen}
            isLoading={messageState?.isAnalyticsLoading}
            data={messageState?.analyticsData}
            postName={post.name}
            onClose={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsModalOpen(false);
            }}
          />
        )}
      </div>
    </ItemUpdateMessageContentWrapper>
  );
};

export default React.memo(PostItem);
